import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useGoogleLogin } from '@react-oauth/google';
import AuthLayout from "../../components/layouts/auth-layout";
import SideImage from "../../assets//images/sideImage.png"
import Input from "../../components/input";
import { PasswordLockIcon, ReloadIcon, SmallMailIcon } from "../../assets/icon";
import Button from "../../components/button";
import useRequest from "../../components/hook/use-req";
import { ClockLoader } from "react-spinners";
import { toast } from "sonner";
import PasswordCriteria from "../../components/password-criteria";

// ── Google G SVG ────────────────────────────────────────────────────────────
// const GoogleIcon = () => (
//     <img
//         src="https://www.svgrepo.com/show/475656/google-color.svg"
//         alt="Google"
//         className="w-5 h-5 mr-3"
//     />
// );

// ─── CreateAccount ─────
const CreateAccount = () => {
    const { handleSubmit, control, watch } = useForm();
    const navigate = useNavigate();
    const [viewPassword, setViewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { loading, makeRequest } = useRequest("/auth/signup", "POST");
    const { loading: googleLoader, makeRequest: googleAuth } = useRequest("/auth/google", "POST");
    const passwordValue = watch("password");

    const handleCreateAccount = handleSubmit(async (formData) => {
        const login = {
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            password: formData.password,
        };
        const [response] = await makeRequest(login);
        if (response?.success) {
            const user = response?.user;
            toast.success(response?.message || 'Signup successful');
            localStorage.setItem("userDetails", JSON.stringify(user));
            localStorage.setItem("authToken", response?.data?.access_token);
            navigate('/verify')
            return;
        }
        toast.error(response?.detail?.error?.message || response.detail || 'An error occured, try again');
    });

    // Google Sign-In handler
    // const handleGoogleSignIn = useGoogleLogin({
    //     onSuccess: async (tokenResponse) => {
    //         try {
    //             // Send the Google token to your backend
    //             const [response] = await googleAuth({
    //                 access_token: tokenResponse.access_token,
    //             });

    //             if (response?.success) {
    //                 const user = response?.user;
    //                 toast.success(response?.message || 'Google signup successful');
    //                 localStorage.setItem("userDetails", JSON.stringify(user));
    //                 localStorage.setItem("authToken", response?.data?.access_token);
                    
    //                 // Check if user needs to complete profile or verify email
    //                 if (response?.data?.is_new_user) {
    //                     // New user - maybe redirect to complete profile
    //                     navigate('/complete-profile');
    //                 } else {
    //                     // Existing user - redirect to dashboard
    //                     navigate('/dashboard');
    //                 }
    //             } else {
    //                 toast.error(response?.detail?.error?.message || response.detail || 'Google signup failed');
    //             }
    //         } catch (error) {
             
    //             toast.error('Failed to authenticate with Google');
    //         }
    //     },
    //     onError: (errorResponse) => {

    //         toast.error('Google sign-in failed. Please try again.');
    //     },
    //     flow: 'implicit', // or 'auth-code' depending on your backend setup
    // });

    // Alternative: If you need to get user profile information
    const handleGoogleSignInWithProfile = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                // Fetch user profile from Google
                const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                    headers: {
                        Authorization: `Bearer ${tokenResponse.access_token}`,
                    },
                });
                
                const userInfo = await userInfoResponse.json();
                
                // Send both token and user info to your backend
                const [response] = await googleAuth({
                    access_token: tokenResponse.access_token,
                    email: userInfo.email,
                    first_name: userInfo.given_name,
                    last_name: userInfo.family_name,
                    google_id: userInfo.sub,
                    profile_picture: userInfo.picture,
                });

                if (response?.success) {
                    const user = response?.user;
                    toast.success(response?.message || 'Google signup successful');
                    localStorage.setItem("userDetails", JSON.stringify(user));
                    localStorage.setItem("authToken", response?.data?.access_token);
                    
                    if (response?.data?.is_new_user) {
                        navigate('/complete-profile');
                    } else {
                        navigate('/dashboard');
                    }
                }
            } catch (error) {

                toast.error('Failed to authenticate with Google');
            }
        },
        onError: (errorResponse) => {
    
            toast.error('Google sign-in failed. Please try again.');
        },
    });

    const handleShowPassword = () => setViewPassword((prev) => !prev);

    const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
    };

    return (
        <AuthLayout sideImageSrc={SideImage}>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-[32px] font-bold text-[#3A3428] dark:text-gray-100">Welcome to <span className="font-medium">ConvoHub</span></h1>
                <p className="text-[16px] text-[#434343] dark:text-gray-400">Reach customers on every channel, effortlessly.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateAccount} className="space-y-4">
                <h6 className="text-[24px] font-medium text-[#3A3428] dark:text-gray-100">Sign up</h6>

                {/* Name Fields */}
                <div className="flex gap-3">
                    <Controller
                        name="first_name"
                        control={control}
                        rules={{
                            required: "First name is required",
                            minLength: {
                                value: 3,
                                message: "First name must be at least 3 characters",
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <Input
                                type="text"
                                label='First Name'
                                placeholder="Enter first name"
                                value={field.value}
                                error={fieldState?.error?.message}
                                onChange={field.onChange}
                                className="w-full"
                            />
                        )}
                    />

                    <Controller
                        name="last_name"
                        control={control}
                        rules={{
                            required: "Last name is required",
                            minLength: {
                                value: 3,
                                message: "Last name must be at least 3 characters",
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <Input
                                type="text"
                                label='Last Name'
                                placeholder="Enter last name"
                                value={field.value}
                                error={fieldState?.error?.message}
                                onChange={field.onChange}
                                className="w-full"
                            />
                        )}
                    />
                </div>

                {/* Email */}
                <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    rules={{
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Enter a valid email.",
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <Input
                            label="Email address"
                            value={field.value}
                            icon={<SmallMailIcon />}
                            placeholder="Enter your email"
                            className="w-full "
                            error={fieldState?.error?.message}
                            onChange={field.onChange}
                        />
                    )}
                />
                
                {/* Password */}
                <Controller
                    name="password"
                    control={control}
                    rules={{
                        required: "Password is required",
                        pattern: {
                            value: /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                            message: "Password must be at least 8 characters long, include a number and a special character",
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <div className="w-full relative">
                            <Input
                                label="Password"
                                icon={<PasswordLockIcon />}
                                type={viewPassword ? "text" : "password"}
                                value={field.value}
                                placeholder="Enter a Password"
                                className="w-full mb-4"
                                onChange={field.onChange}
                            />
                            <button
                                onClick={handleShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                                type="button"
                            >
                                {!viewPassword ? "Show" : "Close"}
                            </button>
                            {passwordValue?.length > 0 && (
                                <PasswordCriteria password={passwordValue} />
                            )}
                            {fieldState?.error && (
                                <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">
                                    {fieldState.error.message}
                                </p>
                            )}
                        </div>
                    )}
                />

                {/* Confirm Password */}
                <Controller
                    name="confirm_password"
                    control={control}
                    rules={{
                        required: "Confirm password is required",
                        validate: (value) =>
                            value === watch("password") || "Passwords do not match",
                    }}
                    render={({ field, fieldState }) => (
                        <div className="w-full relative">
                            <Input
                                label="Confirm Password"
                                icon={<ReloadIcon />}
                                type={showConfirmPassword ? "text" : "password"}
                                value={field.value}
                                placeholder="Re-enter Password"
                                className="w-full mb-4"
                                onChange={field.onChange}
                            />
                            <button
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                                type="button"
                            >
                                {!showConfirmPassword ? "Show" : "Close"}
                            </button>
                            {fieldState?.error && (
                                <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">
                                    {fieldState.error.message}
                                </p>
                            )}
                        </div>
                    )}
                />

                {/* Submit */}
                <Button
                    size="lg"
                    variant="primary"
                    className="w-full mt-6"
                    type="submit"
                >
                    {loading ? (
                        <ClockLoader color="#ffffff" loading={loading} size={20} />
                    ) : (
                        "Send Verification Code"
                    )}
                </Button>
            </form>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">OR</span>
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
            </div>

            {/* Google Button */}
            {/* <button
                type="button"
                onClick={() => handleGoogleSignIn()}
                disabled={googleLoader}
                className="flex items-center justify-center w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium bg-white hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {googleLoader ? (
                    <ClockLoader color="#4338CA" loading={googleLoader} size={20} />
                ) : (
                    <>
                        <GoogleIcon />
                        Continue with Google
                    </>
                )}
            </button> */}

            {/* Sign In Link */}
            <p className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400">
                Already have an account?{" "}
                <Link to="/login" className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors">
                    Sign In
                </Link>
            </p>
        </AuthLayout>
    );
};

export default CreateAccount;